import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import * as PIXI from "pixi.js";
import { useDispatch, useSelector } from "react-redux";
import {
  selectRoyalCollectActive,
  selectFeatureSpinComplete,
  selectRoyalCollectSymbolsReady,
  selectRoyalCollectReelsReady,
  selectRoyalCollectOverlayReelsReady,
  selectSceneTransitionActive,
  selectBet,
} from "../State/selectors";
import {
  setFeatureSpinComplete,
  setFeatureSpinActive,
  featureSpinManualStop,
  rgsPlay,
  setCriticalAnimationPlaying,
  applyRoyalCollectPendingRespins,
  setShowRoyalCollectOutro,
} from "../State/actions";
import store from "../State/Store/store";
import { useReelRefs } from "./ReelRefsContext";
import { useDimensions } from "./DimensionsContext";
import { FEATURES_CONFIG } from "../featuresConfig";
import { GAME_LAYOUT, Settings } from "../settings";
import { SOUND_NAME } from "../sounds";
import { soundManager } from "../SoundManager";
import {
  symbolData,
  isCoinSymbol,
  isJackpotSymbol,
  isRoyalSymbol,
  isBonusSymbol,
  getJackpotTier,
} from "../SymbolData";
import { createPlayButtonRotationAnimation, formatToCustomLocale } from "../utilities";
import {
  selectRoyalCollectInitialReels,
} from "../State/selectors";

/**
 * RoyalCollectControlContext
 *
 * Orchestrates the Royal Collect feature spin loop:
 * - All blank symbols spin simultaneously
 * - Cascading stops with configurable delays
 * - Autoplay: spins automatically, pauses during collection, stops when respins exhausted
 *
 * Clean implementation based on ReelControllerV2 patterns.
 * Autoplay mirrors HoldAndWinControlContext's two-effect approach.
 */
// Alternating arc directions for royal collection travel paths (left, right, left)
const ARC_DIRECTIONS = ["left", "right", "left"];

export const RoyalCollectControlContext = createContext(null);

export const RoyalCollectControlContextProvider = ({ children }) => {
  const {
    royalCollectReelRefs,
    royalCollectOverlayReelRefs,
    royalCollectCollectionLayerRef,
    playButtonRef,
    rcJackpotsControllerRef,
  } = useReelRefs();

  const { symbolHeight } = useDimensions();
  const dispatch = useDispatch();

  // Spin tracking refs
  const spinningCountRef = useRef(0);
  const completedCountRef = useRef(0);
  const isSpinningRef = useRef(false);
  const autoStopTimeoutRef = useRef(null);
  const stopTimeoutsRef = useRef(new Map());
  const prevSpinCompleteRef = useRef(false);
  const readyInitializedRef = useRef(false);
  const manualStopPendingRef = useRef(false);
  const manualStopUnsubRef = useRef(null);
  const isCollectingRef = useRef(false);
  const initialCollectionDoneRef = useRef(false);
  const componentsEverReadyRef = useRef(false); // Persistent: once RC components report ready, they stay mounted
  const coinLandedThisSpinRef = useRef(false); // Track first coin landing per spin
  const windUpsCompleteRef = useRef(true); // Track wind-up cascade completion (AUTO speed only)

  // Autoplay refs
  const handleSpinRef = useRef(null); // Latest handleSpin (avoids stale closures in callbacks)
  const autoplayTimeoutRef = useRef(null); // Current scheduled autoplay timeout (for cleanup)



  const royalSymbolIndexRef = useRef(null); // Symbol index of the royal on the middle reel
  const royalSymbolIdRef = useRef(null); // Symbol ID of the royal (e.g., 'r1') — persisted across spins
  const knownRoyalsRef = useRef(new Map()); // All known royals: symbolIndex → symbolId
  const royalValuesRef = useRef(new Map()); // Per-royal accumulated values: symbolIndex → total
  const counterTickersRef = useRef(new Map()); // Per-royal counter tickers: symbolIndex → tickerFn

  // Redux selectors
  const royalCollectActive = useSelector(selectRoyalCollectActive);
  const featureSpinComplete = useSelector(selectFeatureSpinComplete);
  const sceneTransitionActive = useSelector(selectSceneTransitionActive);
  const royalCollectSymbolsReady = useSelector(selectRoyalCollectSymbolsReady);
  const royalCollectReelsReady = useSelector(selectRoyalCollectReelsReady);
  const royalCollectOverlayReelsReady = useSelector(
    selectRoyalCollectOverlayReelsReady,
  );
  const bet = useSelector(selectBet);
  const initialReels = useSelector(selectRoyalCollectInitialReels);

  // ── Schedule Next Spin (mechanical trigger) ──────────────────────────
  // Plain function — only reads refs and store, no React state deps.
  // Called directly from collection callbacks and spin-complete handlers.
  // Each phase triggers the next deterministically — no React effect timing.

  const scheduleNextSpin = (isFirstSpin = false) => {
    // Clear any existing scheduled spin
    if (autoplayTimeoutRef.current) {
      clearTimeout(autoplayTimeoutRef.current);
      autoplayTimeoutRef.current = null;
    }

    // Check if feature should continue
    const state = store.getState();
    const respinsLeft = state.featureGame.royalCollectRespinsLeft;
    if (!isFirstSpin && respinsLeft <= 0) {
      // Feature ending — don't schedule another spin
      return;
    }

    const rcConfig = FEATURES_CONFIG.ROYAL_COLLECT;
    const delay = isFirstSpin
      ? (rcConfig.AUTOPLAY_FIRST_SPIN_DELAY || 800)
      : (rcConfig.AUTOPLAY_SPIN_DELAY || 800);

    autoplayTimeoutRef.current = setTimeout(() => {
      autoplayTimeoutRef.current = null;

      // Final guard: respins remain
      const currentState = store.getState();
      const currentRespins = currentState.featureGame.royalCollectRespinsLeft;
      if (currentRespins > 0 && handleSpinRef.current) {
        handleSpinRef.current();
      }
    }, delay);
  };

  // ── World Position Helper ──────────────────────────────────────────────
  // Computes the local position of a symbol within the collection layer's
  // coordinate space. Since the collection layer is a sibling of the overlay
  // reelset, both share the same parent — so overlay container positions
  // can be used directly.

  const getSymbolLocalPosition = useCallback(
    (reelIndex, symbolIndex) => {
      const overlayReelRef =
        royalCollectOverlayReelRefs.current?.[reelIndex];
      if (!overlayReelRef) return null;

      // The overlay symbol ref has a container with x/y set relative to overlay reel
      const overlaySymbolRef = overlayReelRef.getSymbolRef?.(symbolIndex);
      const controller = overlaySymbolRef?.getController?.();
      if (!controller?.overlaySymbolContainer) return null;

      // Both the overlay reelset and collection layer share the same parent (feature_reel_container).
      // The overlay symbol container Y is relative to its reel, and the reel X is relative to the reelset.
      // Add symbolHeight/2 because spine origins are center-based but the collection
      // layer is top-aligned (shifted up by symbolHeight/2 vs the overlay reelset).
      const container = controller.overlaySymbolContainer;
      const reelContainer = container.parent; // rc_overlay_reel_N

      return {
        x: reelContainer?.x ?? 0,
        y: container.y + symbolHeight / 2,
      };
    },
    [royalCollectOverlayReelRefs, symbolHeight],
  );

  // ── Collect Coins ──────────────────────────────────────────────────────
  // Scans the grid for collectible symbols and triggers sequential collection.

  const triggerCollection = useCallback(
    (sourceReels, onComplete) => {
      const collectionLayer = royalCollectCollectionLayerRef.current;
      if (!collectionLayer || isCollectingRef.current) {
        onComplete?.();
        return;
      }

      // Build royal positions: start with cached indices, then scan for any new royals.
      // Always compute positions fresh from getSymbolLocalPosition (resize-safe).
      const royalPositions = [];
      const seenIndices = new Set();

      // Always include cached royal(s) from previous calls
      if (royalSymbolIndexRef.current !== null) {
        const freshPos = getSymbolLocalPosition(GAME_LAYOUT.MIDDLE_REEL, royalSymbolIndexRef.current);
        if (freshPos) {
          royalPositions.push({
            position: freshPos,
            symbolIndex: royalSymbolIndexRef.current,
            symbolId: royalSymbolIdRef.current,
          });
          seenIndices.add(royalSymbolIndexRef.current);
        }
      }

      // Scan for any NEW royals that landed this spin
      for (
        let symbolIndex = 0;
        symbolIndex < GAME_LAYOUT.SYMBOLS_VISIBLE;
        symbolIndex++
      ) {
        if (seenIndices.has(symbolIndex)) continue;
        const symId = sourceReels?.[GAME_LAYOUT.MIDDLE_REEL]?.[symbolIndex];
        if (isRoyalSymbol(symId)) {
          const pos = getSymbolLocalPosition(GAME_LAYOUT.MIDDLE_REEL, symbolIndex);
          if (pos) {
            royalPositions.push({ position: pos, symbolIndex, symbolId: symId });
            seenIndices.add(symbolIndex);
          }
        }
      }

      // Cache the first royal's index/id if not yet cached (position always computed fresh)
      if (royalPositions.length > 0 && royalSymbolIndexRef.current === null) {
        royalSymbolIndexRef.current = royalPositions[0].symbolIndex;
        royalSymbolIdRef.current = royalPositions[0].symbolId;
      }

      // Track ALL royals (for royal collection at feature end)
      for (const rp of royalPositions) {
        knownRoyalsRef.current.set(rp.symbolIndex, rp.symbolId);
      }

      if (royalPositions.length === 0) {
        onComplete?.();
        return;
      }

      // Gather collectible coins with textures from symbol controllers
      const coins = [];
      for (let reelIndex = 0; reelIndex < sourceReels.length; reelIndex++) {
        if (reelIndex === GAME_LAYOUT.MIDDLE_REEL) continue;
        const reel = sourceReels[reelIndex];
        if (!Array.isArray(reel)) continue;

        for (
          let symbolIndex = 0;
          symbolIndex < Math.min(reel.length, GAME_LAYOUT.SYMBOLS_VISIBLE);
          symbolIndex++
        ) {
          const symbolId = reel[symbolIndex];
          if (!isCoinSymbol(symbolId) && !isJackpotSymbol(symbolId)) continue;

          const reelRef = royalCollectReelRefs.current?.[reelIndex];
          const symRef = reelRef?.getSymbolRef?.(symbolIndex);
          const symbolCtrl = symRef?.current?.getSymbolController?.();
          const overlayReelRef = royalCollectOverlayReelRefs.current?.[reelIndex];
          const overlaySymbolRef = overlayReelRef?.getSymbolRef?.(symbolIndex);

          const symbolInfo = symbolData[symbolId];
          if (!symbolInfo?.spine) continue;

          // Spine info for traveling animation
          const spineName = symbolInfo.spine; // e.g., "c" or "j"
          const skinName = symbolInfo.skin || symbolInfo.name; // e.g., "c5" or "j1"

          // Compute value text from the immutable source of truth (symbolInfo.value × bet).
          // Never read from live PIXI objects which can be stale or contaminated.
          let valueTextData = null;

          if (symbolInfo?.value && isCoinSymbol(symbolId)) {
            const state = store.getState();
            const currentBet = state?.game?.bet || 1;
            const coinValue = parseInt(symbolInfo.value, 10);
            if (!isNaN(coinValue) && coinValue > 0) {
              const displayValue = formatToCustomLocale(coinValue * currentBet);
              const fontSize = symbolCtrl?.symbolHeight 
                ? symbolCtrl.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER
                : 30;
              valueTextData = {
                text: displayValue,
                fontName: 'font_secondary_scaled',
                fontSize,
                y: (symbolCtrl?.symbolHeight || 168) * (82.67 / 338),
                scaleX: 1,
                scaleY: 1,
              };
            }
          }

          // Get overlay controller for frame-matching during travel
          const overlayCtrl = overlaySymbolRef?.getController?.();

          coins.push({
            symbolId,
            spineName,
            skinName,
            overlayController: overlayCtrl,
            valueTextData,
            startPos: getSymbolLocalPosition(reelIndex, symbolIndex),
            hideOriginal: () => {
              // Hide overlay spine — the only visible element after landing.
              // (Static texture + spinning sprites are already hidden by
              // handleSymbolStopComplete → hideSymbolTexture / _finalizeLanding)
              if (overlaySymbolRef?.hideAnimation) {
                overlaySymbolRef.hideAnimation();
              }
              // Mark controller so next spin starts with empty space
              if (symbolCtrl) {
                symbolCtrl.collectedBlank = true;
              }
            },
          });
        }
      }

      if (coins.length === 0) {
        onComplete?.();
        return;
      }

      isCollectingRef.current = true;
      dispatch(setCriticalAnimationPlaying(true));

      // Read speed config for travel duration
      const rcConfig = FEATURES_CONFIG.ROYAL_COLLECT;
      const currentSpeed = store.getState().settings.autoPlaySpeed || "auto";
      const collectionSpeedConfig = rcConfig.AUTO_PLAY_SPEEDS[currentSpeed.toUpperCase()] || rcConfig.AUTO_PLAY_SPEEDS.AUTO;

      // Helper: send a single coin to a single royal
      const sendCoinToRoyal = (coin, royalPos, royalIdx, royalSymId, shouldHide) => {
        return new Promise((resolve) => {
          const singleCoin = {
            ...coin,
            hideOriginal: shouldHide ? coin.hideOriginal : () => {},
          };

          collectionLayer.collectCoins([singleCoin], royalPos, {
            speedConfig: collectionSpeedConfig,
            onCoinCollected: (symbolId) => {
              // Accumulate the coin's value into THIS royal's own counter
              const coinInfo = symbolData[symbolId];
              if (coinInfo?.value) {
                const coinValue = parseInt(coinInfo.value, 10);
                if (!isNaN(coinValue) && coinValue > 0) {
                  const currentBet = store.getState()?.game?.bet || 1;
                  const previousValue = royalValuesRef.current.get(royalIdx) || 0;
                  const newValue = previousValue + coinValue * currentBet;
                  royalValuesRef.current.set(royalIdx, newValue);

                  // Animate counter on the target royal
                  const overlayReelRef = royalCollectOverlayReelRefs.current?.[GAME_LAYOUT.MIDDLE_REEL];
                  const overlaySymbolRef = overlayReelRef?.getSymbolRef?.(royalIdx);
                  const royalCtrl = overlaySymbolRef?.getController?.();

                  // Play collect animation on royal when coin arrives
                  if (overlaySymbolRef?.showCollectAnimation && royalSymId != null) {
                    overlaySymbolRef.showCollectAnimation(royalSymId, false);
                  }

                  // Update counter on royal
                  if (royalCtrl) {
                    const spineInstance = royalCtrl.activeSpineKey
                      ? royalCtrl.prePositionedSpines.get(royalCtrl.activeSpineKey)
                      : null;

                    if (spineInstance) {
                      if (!royalCtrl.coinValueContainer) {
                        const result = royalCtrl.createCoinValueText(
                          formatToCustomLocale(previousValue || 0)
                        );
                        if (result) {
                          royalCtrl.coinValueContainer = result.counterContainer;
                          royalCtrl.coinValueBitmapText = result.bitmapText;
                          royalCtrl._coinValueSpine = spineInstance;

                          // Counter both spine PIXI scale and bone world scale
                          const spineScale = spineInstance.scale.x || 1;
                          spineInstance.update(0);
                          const valueBone = spineInstance.skeleton?.findBone("value");
                          const boneBaseScale = Math.abs(valueBone?.a) || 1;
                          const totalScale = spineScale * boneBaseScale;

                          if (totalScale && totalScale !== 0 && result.bitmapText) {
                            result.bitmapText.fontSize = result.bitmapText.fontSize / totalScale;
                            result.bitmapText.scale.set(1);
                            result.bitmapText.updateText();

                            const maxTextWidth = (royalCtrl.symbolWidth * (1 - 0.12 * 2)) / totalScale;
                            if (result.bitmapText.width > maxTextWidth) {
                              result.bitmapText.scale.set(maxTextWidth / result.bitmapText.width);
                            }
                          }

                          spineInstance.addSlotObject("txt_slot_value", royalCtrl.coinValueContainer);
                        }
                      }

                      // Cancel previous counter ticker for THIS royal
                      const prevTicker = counterTickersRef.current.get(royalIdx);
                      if (prevTicker) {
                        PIXI.Ticker.shared.remove(prevTicker);
                        counterTickersRef.current.delete(royalIdx);
                      }

                      // Counter duration scales with travel speed:
                      // TURBO = instant (0ms), others = 50% of travel duration
                      const speedKey = (store.getState().settings.autoPlaySpeed || "auto").toUpperCase();
                      const isTurbo = speedKey === "TURBO";
                      const travelMs = collectionSpeedConfig.COIN_TRAVEL_DURATION || 565;
                      const counterDuration = isTurbo ? 0 : travelMs * 0.5;
                      let counterElapsed = 0;
                      // TURBO: instant update — no ticker needed
                      if (counterDuration <= 0) {
                        if (royalCtrl.coinValueBitmapText) {
                          royalCtrl.coinValueBitmapText.text = formatToCustomLocale(newValue);
                          royalCtrl.autoFitCoinValueText();
                        }
                      } else {
                        const counterTick = () => {
                          counterElapsed += PIXI.Ticker.shared.deltaMS;
                          const t = Math.min(counterElapsed / counterDuration, 1);
                          const eased = 1 - (1 - t) * (1 - t);
                          const currentValue = Math.floor(
                            previousValue + (newValue - previousValue) * eased
                          );
                          if (royalCtrl.coinValueBitmapText) {
                            royalCtrl.coinValueBitmapText.text = formatToCustomLocale(currentValue);
                            royalCtrl.autoFitCoinValueText();
                          }
                          if (t >= 1) {
                            if (royalCtrl.coinValueBitmapText) {
                              royalCtrl.coinValueBitmapText.text = formatToCustomLocale(newValue);
                              royalCtrl.autoFitCoinValueText();
                            }
                            PIXI.Ticker.shared.remove(counterTick);
                            counterTickersRef.current.delete(royalIdx);
                          }
                        };

                        counterTickersRef.current.set(royalIdx, counterTick);
                        PIXI.Ticker.shared.add(counterTick);
                      }
                    }
                  }
                }
              }
            },
            onAllComplete: () => resolve(),
          });
        });
      };

      // Per-coin, per-royal dispatch — speed-dependent sequencing
      // Three modes based on STAGGER_RATIO:
      //   undefined/absent → sequential (AUTO: wait for each coin to finish)
      //   0 < ratio < 1   → overlapping (QUICK: next coin starts after ratio% of travel)
      //   0               → simultaneous (TURBO: all coins fire at once)
      const runAllPasses = async () => {
        const staggerRatio = collectionSpeedConfig.STAGGER_RATIO;
        const travelDuration = collectionSpeedConfig.COIN_TRAVEL_DURATION || 565;

        // Build flat list of all coin→royal sends
        const sends = [];
        for (let coinIdx = 0; coinIdx < coins.length; coinIdx++) {
          const coin = coins[coinIdx];
          for (let royalIdx = 0; royalIdx < royalPositions.length; royalIdx++) {
            const isLastRoyalForThisCoin = royalIdx === royalPositions.length - 1;
            sends.push({ coin, royalIdx, isLastRoyalForThisCoin });
          }
        }

        if (staggerRatio === 0) {
          // TURBO: fire ALL simultaneously
          await Promise.all(
            sends.map(({ coin, royalIdx, isLastRoyalForThisCoin }) =>
              sendCoinToRoyal(
                coin,
                royalPositions[royalIdx].position,
                royalPositions[royalIdx].symbolIndex,
                royalPositions[royalIdx].symbolId,
                isLastRoyalForThisCoin
              )
            )
          );
        } else if (staggerRatio != null && staggerRatio > 0 && staggerRatio < 1) {
          // QUICK: overlapping — each coin starts after staggerRatio × travelDuration
          const staggerDelay = travelDuration * staggerRatio;
          const allPromises = [];

          for (let i = 0; i < sends.length; i++) {
            const { coin, royalIdx, isLastRoyalForThisCoin } = sends[i];

            // Delay each send by i * staggerDelay
            const delayedSend = new Promise((resolve) => {
              setTimeout(() => {
                sendCoinToRoyal(
                  coin,
                  royalPositions[royalIdx].position,
                  royalPositions[royalIdx].symbolIndex,
                  royalPositions[royalIdx].symbolId,
                  isLastRoyalForThisCoin
                ).then(resolve);
              }, i * staggerDelay);
            });

            allPromises.push(delayedSend);
          }

          await Promise.all(allPromises);
        } else {
          // AUTO: fully sequential — wait for each coin to finish
          for (const { coin, royalIdx, isLastRoyalForThisCoin } of sends) {
            await sendCoinToRoyal(
              coin,
              royalPositions[royalIdx].position,
              royalPositions[royalIdx].symbolIndex,
              royalPositions[royalIdx].symbolId,
              isLastRoyalForThisCoin
            );
          }
        }

        // All coins have traveled — wait for royal counter tickers to finish
        // before signaling completion. Counter tickers run for ~600ms after each
        // coin arrives; the map will be empty once all counters settle.
        const waitForCounters = () => {
          if (counterTickersRef.current.size === 0) {
            isCollectingRef.current = false;
            dispatch(setCriticalAnimationPlaying(false));
            onComplete?.();
          } else {
            setTimeout(waitForCounters, 50);
          }
        };
        waitForCounters();
      };

      runAllPasses();
    },
    [
      dispatch,
      royalCollectCollectionLayerRef,
      royalCollectReelRefs,
      royalCollectOverlayReelRefs,
      getSymbolLocalPosition,
    ],
  );

  // ── Royal Collection (Feature End) ──────────────────────────────────────
  // After all respins are exhausted and coin collections finish, collect
  // royal symbols from the middle reel → travel to respin counter.


  const triggerRoyalCollection = useCallback(
    (onComplete) => {
      const collectionLayer = royalCollectCollectionLayerRef.current;
      if (!collectionLayer || isCollectingRef.current) {
        onComplete?.();
        return;
      }

      // Build royal positions from knownRoyalsRef (populated during coin collection)
      const royalPositions = [];
      const overlayReelRef = royalCollectOverlayReelRefs.current?.[GAME_LAYOUT.MIDDLE_REEL];
      const middleReelRef = royalCollectReelRefs.current?.[GAME_LAYOUT.MIDDLE_REEL];

      for (const [symbolIndex, symbolId] of knownRoyalsRef.current) {
        const symbolInfo = symbolData[symbolId];
        const startPos = getSymbolLocalPosition(GAME_LAYOUT.MIDDLE_REEL, symbolIndex);

        if (!symbolInfo?.spine || !startPos) continue;

        const overlaySymbolRef = overlayReelRef?.getSymbolRef?.(symbolIndex);
        const symRef = middleReelRef?.getSymbolRef?.(symbolIndex);
        const overlayCtrl = overlaySymbolRef?.getController?.();

        // Clone accumulated value text (same pattern as coins with valueTextData)
        let valueTextData = null;
        const accumulatedValue = royalValuesRef.current.get(symbolIndex) || 0;
        if (accumulatedValue > 0) {
          const displayValue = formatToCustomLocale(accumulatedValue);
          const symbolCtrl = symRef?.current?.getSymbolController?.();
          const fontSize = symbolCtrl?.symbolHeight
            ? symbolCtrl.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER
            : 30;
          valueTextData = {
            text: displayValue,
            fontName: 'font_secondary_scaled',
            fontSize,
            y: (symbolCtrl?.symbolHeight || 168) * (82.67 / 338),
            scaleX: 1,
            scaleY: 1,
          };
        }

        royalPositions.push({
          symbolId,
          symbolIndex,
          spineName: symbolInfo.spine,
          skinName: symbolInfo.skin || symbolInfo.name,
          overlayController: overlayCtrl,
          valueTextData,
          startPos,
          hideOriginal: () => {
            overlaySymbolRef?.hideAnimation?.();
            symRef?.current?.hideSymbolTexture?.();
          },
        });
      }

      if (royalPositions.length === 0) {
        onComplete?.();
        return;
      }

      // Assign alternating arc directions: left, right, left
      const royals = royalPositions.map((royal, i) => ({
        ...royal,
        arcDirection: ARC_DIRECTIONS[i % ARC_DIRECTIONS.length],
      }));

      // Destination: top edge of the top symbol (index 0) on the middle reel.
      // getSymbolLocalPosition returns the CENTER of the symbol cell (spine origin is center).
      // Subtract half a symbolHeight to get the top edge.
      const topSymbolPos = getSymbolLocalPosition(GAME_LAYOUT.MIDDLE_REEL, 0);

      const counterPos = topSymbolPos
        ? { x: topSymbolPos.x, y: topSymbolPos.y - symbolHeight * 0.65 }
        : { x: 0, y: 0 }; // Fallback

      isCollectingRef.current = true;
      dispatch(setCriticalAnimationPlaying(true));

      const rcConfig = FEATURES_CONFIG.ROYAL_COLLECT;
      const currentSpeed = store.getState().settings.autoPlaySpeed || "auto";
      const collectionSpeedConfig = rcConfig.AUTO_PLAY_SPEEDS[currentSpeed.toUpperCase()] || rcConfig.AUTO_PLAY_SPEEDS.AUTO;

      collectionLayer.collectRoyals(royals, counterPos, {
        speedConfig: collectionSpeedConfig,
        onRoyalCollected: () => {},
        onAllComplete: () => {
          isCollectingRef.current = false;
          dispatch(setCriticalAnimationPlaying(false));
          onComplete?.();
        },
      });
    },
    [
      dispatch,
      royalCollectCollectionLayerRef,
      royalCollectReelRefs,
      royalCollectOverlayReelRefs,
      getSymbolLocalPosition,
      symbolHeight,
    ],
  );

  // ── Get Spinnable Symbols ───────────────────────────────────────────────
  // All symbols spin except the permanent royal on the middle reel (index 1)

  const getBlankSymbols = useCallback(() => {
    const spinnableSymbols = [];
    const reels = royalCollectReelRefs.current;
    if (!reels || !Array.isArray(reels)) return spinnableSymbols;

    reels.forEach((reel, reelIndex) => {
      if (!reel) return;

      for (
        let symbolIndex = 0;
        symbolIndex < GAME_LAYOUT.SYMBOLS_VISIBLE;
        symbolIndex++
      ) {
        const symbolRef = reel.getSymbolRef?.(symbolIndex);
        if (!symbolRef?.current) continue;

        const isReady = symbolRef.current.isControllerReady?.();
        if (!isReady) continue;

        const controller = symbolRef.current.getSymbolController?.();
        if (!controller) continue;

        // All symbols spin except the permanent royal on the middle reel
        if (!controller.isPermanentRoyal) {
          spinnableSymbols.push({ reelIndex, symbolIndex, symbolRef, controller });
        }
      }
    });

    return spinnableSymbols;
  }, [royalCollectReelRefs]);

  // ── Cascading Stops ───────────────────────────────────────────────────
  // Mirrors ReelControlContext.stopReels() — two timing modes:
  // - Auto stop: wider cascade (SYMBOL_STOP_CASCADE_DELAY = 300ms) for natural feel
  // - Manual stop: tight cascade (SYMBOL_STOP_CASCADE_DELAY_MANUAL = 50ms) for instant feedback

  const triggerCascadingStops = useCallback((isManualStop = false) => {
    const blankSymbols = [];
    const deferredSymbols = [];
    const reels = royalCollectReelRefs.current;
    if (!reels) return;

    // Clear all existing stop timeouts to prevent conflicts (same as ReelControlContext)
    stopTimeoutsRef.current.forEach((id) => clearTimeout(id));
    stopTimeoutsRef.current.clear();

    // Find all currently spinning OR winding-up symbols
    reels.forEach((reel, reelIndex) => {
      if (!reel) return;
      for (
        let symbolIndex = 0;
        symbolIndex < GAME_LAYOUT.SYMBOLS_VISIBLE;
        symbolIndex++
      ) {
        const symbolRef = reel.getSymbolRef?.(symbolIndex);
        const controller = symbolRef?.current?.getSymbolController?.();
        if (!controller) continue;
        // Include symbols that are spinning OR still in wind-up phase
        const isActive = (controller?.isPlaying || controller?.isWindingUp) && !controller?.isStopping;
        if (isActive) {
          // Only stop symbols that have received API landing data
          if (controller.pendingLandingSymbolId !== null) {
            blankSymbols.push({ reelIndex, symbolIndex, symbolRef, controller });
          } else {
            // No API data yet — defer. The controller will auto-stop
            // when isStopping=true and next wrap occurs after data arrives.
            deferredSymbols.push({ reelIndex, symbolIndex, symbolRef, controller });
          }
        }
      }
    });

    const config = FEATURES_CONFIG.ROYAL_COLLECT;
    // Read speed-dependent cascade config
    const currentSpeed = store.getState().settings.autoPlaySpeed || "auto";
    const speedConfig = config.AUTO_PLAY_SPEEDS[currentSpeed.toUpperCase()] || config.AUTO_PLAY_SPEEDS.AUTO;

    const baseDelay = isManualStop ? 0 : speedConfig.SYMBOL_STOP_BASE_DELAY;
    // Manual stop uses tight cascade for instant feedback,
    // auto stop uses wider cascade for natural feel
    const cascadeDelay = isManualStop
      ? speedConfig.SYMBOL_STOP_CASCADE_DELAY_MANUAL
      : speedConfig.SYMBOL_STOP_CASCADE_DELAY;

    // Stop symbols that have landing data — cascading delays
    blankSymbols.forEach((symbolInfo, index) => {
      const uniqueId = `${symbolInfo.reelIndex}-${symbolInfo.symbolIndex}`;

      const delay = baseDelay + index * cascadeDelay;

      const timeoutId = setTimeout(() => {
        try {
          symbolInfo.symbolRef.current?.stopSpin();
        } catch {
          // Silent fail
        }
        stopTimeoutsRef.current.delete(uniqueId);
      }, delay);

      stopTimeoutsRef.current.set(uniqueId, timeoutId);
    });

    // Mark deferred symbols as stopping — they'll execute the stop
    // on their next wrap cycle once API data is set via setLandingSymbolId()
    deferredSymbols.forEach((symbolInfo) => {
      symbolInfo.controller.isStopping = true;
    });
  }, [royalCollectReelRefs]);

  // ── Symbol Stop Complete ──────────────────────────────────────────────

  const handleSymbolStopComplete = useCallback(
    (symbolIndex, reelIndex) => {
      completedCountRef.current++;

      // Trigger overlay landing animation for bonus symbols (same pattern as H&W)
      const overlayReelRef = royalCollectOverlayReelRefs.current?.[reelIndex];
      const overlaySymbolRef = overlayReelRef?.getSymbolRef?.(symbolIndex);
      const overlayController = overlaySymbolRef?.getController?.();

      const reelRef = royalCollectReelRefs.current?.[reelIndex];
      const symbolRef = reelRef?.getSymbolRef?.(symbolIndex);
      const controller = symbolRef?.current?.getSymbolController?.();

      // Determine landed symbol
      const landedSymbolId = controller
        ? (controller.pendingLandingSymbolId ?? controller.symbolId)
        : null;

      // Bonus symbols (coins, jackpots, royals) get overlay spine animations.
      // Everything else — blanks, unknowns, nulls — shows the static texture.
      // Using isBonusSymbol() is more robust than checking symbolData[id].spine,
      // because the blank symbol (id=0) has spine:"blank" in symbolData which
      // would incorrectly pass a `symbolInfo?.spine` check.
      const shouldShowOverlay =
        landedSymbolId !== null &&
        landedSymbolId !== undefined &&
        isBonusSymbol(landedSymbolId) &&
        overlayController;

      if (shouldShowOverlay) {
        // Bonus symbol — show landing animation via overlay spine, hide static texture
        overlayController.showLandingAnimation(landedSymbolId);

        // ── Respin counter: apply pending on first coin/jackpot/royal landing ──
        // When first bonus symbol lands, immediately apply the API's pending
        // respins value (typically resets counter from 2→3).
        if (
          !coinLandedThisSpinRef.current &&
          (isCoinSymbol(landedSymbolId) || isJackpotSymbol(landedSymbolId) || isRoyalSymbol(landedSymbolId))
        ) {
          coinLandedThisSpinRef.current = true;
          dispatch(applyRoyalCollectPendingRespins());
        }

        // Trigger jackpot bar hit animation (same as base game)
        if (FEATURES_CONFIG.JACKPOTS.ENABLED && isJackpotSymbol(landedSymbolId)) {
          const tier = getJackpotTier(landedSymbolId);
          const ctrl = rcJackpotsControllerRef?.current;
          if (tier && ctrl) {
            if (tier === "mini") ctrl.playMini();
            else if (tier === "major") ctrl.playMajor();
            else if (tier === "grand") ctrl.playGrand();
          }
        }

        requestAnimationFrame(() => {
          symbolRef?.current?.hideSymbolTexture?.();
        });
      } else {
        // Blank, unknown, or overlay unavailable — show static texture directly.
        // This is the critical path for blank symbols after coin collection:
        // _finalizeLanding() already hid the spinning sprites, so we MUST
        // show the static sprite here or the position goes invisible.
        // Force-update the texture first — after collection, the static sprite's
        // texture may still be PIXI.Texture.EMPTY, which showSymbolTexture() skips.
        symbolRef?.current?.updateSymbolTexture?.(landedSymbolId);
        symbolRef?.current?.showSymbolTexture?.();
      }

      // Check if all symbols have stopped
      if (completedCountRef.current >= spinningCountRef.current) {
        soundManager.stop(SOUND_NAME.feature_reel_spin);
        isSpinningRef.current = false;

        // Build current reels snapshot from landed symbols
        const currentReels = [];
        const reels = royalCollectReelRefs.current;
        if (reels) {
          reels.forEach((reel, ri) => {
            const reelSymbols = [];
            if (reel) {
              for (let si = 0; si < GAME_LAYOUT.SYMBOLS_VISIBLE; si++) {
                const sym = reel.getSymbolRef?.(si);
                const ctrl = sym?.current?.getSymbolController?.();
                const id =
                  ctrl?.pendingLandingSymbolId ?? ctrl?.symbolId ?? 0;
                reelSymbols.push(id);
              }
            }
            currentReels.push(reelSymbols);
          });
        }

        // Check if any new coins/jackpots landed — if so, collect them
        const hasCollectibles = currentReels.some((reel, ri) => {
          if (ri === GAME_LAYOUT.MIDDLE_REEL) return false;
          return reel.some(
            (id) => isCoinSymbol(id) || isJackpotSymbol(id),
          );
        });
        // After coins are collected (or if none), check if feature is ending
        const onPostCollection = () => {
          const currentRespins = store.getState().featureGame.royalCollectRespinsLeft;
          if (currentRespins > 0) {
            // More respins — continue spinning
            dispatch(setFeatureSpinComplete({ complete: true, bet }));
            if (hasCollectibles) {
              // Coins were collected — spin immediately (no artificial delay)
              if (handleSpinRef.current) handleSpinRef.current();
            } else {
              // No coins — wait before next spin so user can process
              scheduleNextSpin();
            }
          } else {
            // Feature ending — collect royal symbols after delay.
            dispatch(setCriticalAnimationPlaying(true));

            const rcCollectionConfig = FEATURES_CONFIG.ROYAL_COLLECT.COLLECTION;
            const delay = rcCollectionConfig.ROYAL_COLLECTION_DELAY || 500;
            setTimeout(() => {
              triggerRoyalCollection(() => {
                dispatch(setFeatureSpinComplete({ complete: true, bet }));
                // Brief pause after collection before showing outro
                setTimeout(() => {
                  dispatch(setShowRoyalCollectOutro(true));
                }, 700);
              });
            }, delay);
          }
        };

        // Always call triggerCollection — even rounds with no coins may have new
        // royals on the middle reel that must be tracked in knownRoyalsRef.
        // triggerCollection scans for royals BEFORE checking for coins, and
        // handles the no-coins path safely (early return via onComplete).
        triggerCollection(currentReels, onPostCollection);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, bet, royalCollectOverlayReelRefs, royalCollectReelRefs, triggerCollection, triggerRoyalCollection, rcJackpotsControllerRef],
  );

  // ── Handle Spin ───────────────────────────────────────────────────────
  // Mirrors ReelControlContext.handleSpin() — rate-limited, resets all state

  const handleSpin = useCallback(() => {
    if (!royalCollectActive) {
      return;
    }
    if (isSpinningRef.current) {
      return;
    }

    // Rate limiting (75ms minimum between spins — same as main game)
    const now = Date.now();
    const timeSinceLastSpin = now - (handleSpin._lastCall || 0);
    if (timeSinceLastSpin < 75) {
      return;
    }
    handleSpin._lastCall = now;

    // Clear any pending auto-stop
    if (autoStopTimeoutRef.current) {
      clearTimeout(autoStopTimeoutRef.current);
      autoStopTimeoutRef.current = null;
    }

    // Clear all pending stop timeouts from previous spin
    stopTimeoutsRef.current.forEach((id) => clearTimeout(id));
    stopTimeoutsRef.current.clear();

    isSpinningRef.current = true;
    completedCountRef.current = 0;
    coinLandedThisSpinRef.current = false; // Reset first-coin-landed flag for new spin

    dispatch(setFeatureSpinComplete({ complete: false, bet }));

    const blankSymbols = getBlankSymbols();
    spinningCountRef.current = blankSymbols.length;

    if (blankSymbols.length === 0) {
      // No blanks to spin — feature should end
      isSpinningRef.current = false;
      dispatch(setFeatureSpinComplete({ complete: true, bet }));
      return;
    }

    // Force-reset internal state of all symbol controllers
    // (mirrors ReelControlContext.handleSpin resetting reel controllers)
    blankSymbols.forEach(({ controller }) => {
      controller.isStopping = false;
      controller.hasStopped = false;
      // Clear stale landing data from previous spin (or initial collection round).
      // Without this, the cascading stop logic sees pendingLandingSymbolId !== null
      // and immediately stops the symbol on the OLD value instead of waiting for
      // the new API response. The new value arrives via setLandingSymbolId() when
      // the royalCollectCurrentRound effect fires after rgsPlay() succeeds.
      controller.pendingLandingSymbolId = null;
    });

    // Hide overlay spine animations and prepare for spin start
    blankSymbols.forEach(({ symbolRef, reelIndex, symbolIndex, controller }) => {
      // Hide overlay spine animation
      const overlayReelRef = royalCollectOverlayReelRefs.current?.[reelIndex];
      const overlaySymbolRef = overlayReelRef?.getSymbolRef?.(symbolIndex);
      if (overlaySymbolRef?.hideAnimation) {
        overlaySymbolRef.hideAnimation();
      }

      // Restore static texture ONLY for non-collected positions.
      // Collected positions stay blank — collectedBlank flag on the controller
      // ensures startSpin() renders the visible sprite as empty space.
      if (!controller.collectedBlank) {
        symbolRef.current?.showSymbolTexture?.();
      }
    });

    // Read current speed mode from Redux and set on all controllers before starting
    const currentSpeed = store.getState().settings.autoPlaySpeed || "auto";
    const config = FEATURES_CONFIG.ROYAL_COLLECT;
    const speedConfig = config.AUTO_PLAY_SPEEDS[currentSpeed.toUpperCase()] || config.AUTO_PLAY_SPEEDS.AUTO;

    // Play button rotation animation
    if (playButtonRef.current) {
      createPlayButtonRotationAnimation(playButtonRef.current);
    }

    // Play spin sound
    soundManager.play(SOUND_NAME.feature_reel_spin);

    // Reset manual stop flag for new spin
    manualStopPendingRef.current = false;

    // Clean up any lingering manual stop subscription from previous spin
    if (manualStopUnsubRef.current) {
      manualStopUnsubRef.current();
      manualStopUnsubRef.current = null;
    }

    // Dispatch RGS play and mark spin active
    // Counter decrement is handled by Redux (rgsPlay.pending decrements royalCollectRespinsLeft)
    dispatch(setFeatureSpinActive(true));
    dispatch(rgsPlay());

    // ── Wind-up cascade (AUTO speed only) or instant start ───────────
    if (speedConfig.WIND_UP_ENABLED) {
      // AUTO speed: cascading wind-up → spin → deferred auto-stop
      windUpsCompleteRef.current = false;
      const cascadeDelay = speedConfig.WIND_UP_CASCADE_DELAY || 30;

      // Set speed on all controllers first
      blankSymbols.forEach(({ controller }) => {
        controller.autoPlaySpeed = currentSpeed;
        controller._speedConfigCache = null;
      });

      // Start each symbol's wind-up with staggered timeouts
      // (same pattern as base game's REEL_START_DELAYS)
      blankSymbols.forEach(({ symbolRef }, i) => {
        const delay = i * cascadeDelay;
        if (delay === 0) {
          symbolRef.current?.startWindUp();
        } else {
          const tid = setTimeout(() => {
            symbolRef.current?.startWindUp();
          }, delay);
          stopTimeoutsRef.current.set(`windup-${i}`, tid);
        }
      });

      // Calculate total wind-up time deterministically
      // (same approach as base game: lastStartDelay + windUpDuration + buffer)
      const s = speedConfig.BOUNCE_OVERSHOOT;
      const spinVelocity = (1 / speedConfig.SPEED_MS_PER_SYMBOL_HEIGHT) * 1000;
      // RC has 1 visible symbol, so stop move distance is always 1
      // (matches STOP_MOVE_DISTANCE constant in RoyalCollectSymbolController)
      const rcStopMoveDistance = 1;
      const totalStopDuration =
        (((s + 3) * rcStopMoveDistance * 1000) / spinVelocity) *
        (FEATURES_CONFIG.ROYAL_COLLECT.STOP_SLOWDOWN_FACTOR || 1.0);
      const bounceFraction = (2 * s) / (3 * (s + 1));
      const windUpPullDuration = Math.round(totalStopDuration * bounceFraction) || 55;
      const windUpReleaseDuration = Math.round(windUpPullDuration * 0.6);
      const singleWindUpDuration = windUpPullDuration + windUpReleaseDuration;
      const lastSymbolStartDelay = (blankSymbols.length - 1) * cascadeDelay;
      const totalWindUpTime = lastSymbolStartDelay + singleWindUpDuration;
      const WIND_UP_CHECK_BUFFER = 50; // Safety margin (ms)
      const checkDelayMs = totalWindUpTime + WIND_UP_CHECK_BUFFER;

      // After all wind-ups guaranteed complete, mark done and schedule auto-stop
      // (mirrors base game's checkPendingStopTimeout pattern)
      const windUpCheckTimeout = setTimeout(() => {
        windUpsCompleteRef.current = true;

        // If manual stop was pressed during wind-up, execute it now
        if (manualStopPendingRef.current) {
          const state = store.getState();
          if (state.spin.featureSpinStatus === "succeeded") {
            triggerCascadingStops(true);
          } else {
            // Data not yet — subscribe (same pattern as handleManualStop)
            const unsubscribe = store.subscribe(() => {
              const currentState = store.getState();
              if (currentState.spin.featureSpinStatus === "succeeded") {
                triggerCascadingStops(true);
                if (manualStopUnsubRef.current) {
                  manualStopUnsubRef.current();
                  manualStopUnsubRef.current = null;
                }
              } else if (currentState.spin.featureSpinStatus === "failed") {
                if (manualStopUnsubRef.current) {
                  manualStopUnsubRef.current();
                  manualStopUnsubRef.current = null;
                }
              }
            });
            manualStopUnsubRef.current = unsubscribe;
          }
          return;
        }

        // No manual stop pending — start auto-stop timeout NOW
        autoStopTimeoutRef.current = setTimeout(() => {
          triggerCascadingStops(false);
        }, speedConfig.SPIN_DURATION);
      }, checkDelayMs);
      stopTimeoutsRef.current.set('windup-check', windUpCheckTimeout);
    } else {
      // QUICK / TURBO: no wind-up — start all symbols immediately
      windUpsCompleteRef.current = true;
      blankSymbols.forEach(({ symbolRef, controller }) => {
        controller.autoPlaySpeed = currentSpeed;
        controller._speedConfigCache = null;
        symbolRef.current?.startSpin();
      });

      // Auto-stop after spin duration
      autoStopTimeoutRef.current = setTimeout(() => {
        triggerCascadingStops(false);
      }, speedConfig.SPIN_DURATION);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    royalCollectActive,
    dispatch,
    getBlankSymbols,
    triggerCascadingStops,
    playButtonRef,
    bet,
    royalCollectOverlayReelRefs,
  ]);

  // ── Manual Stop ──────────────────────────────────────────────────────
  // Mirrors ReelControlContext.handleManualStop() — dispatches manual stop,
  // subscribes to store if API not ready, uses tight cascade on stop

  const handleManualStop = useCallback(() => {
    // Only allow during active spin
    if (!isSpinningRef.current) return;
    if (manualStopPendingRef.current) return; // Already pending

    manualStopPendingRef.current = true;

    // If wind-ups are still in progress, defer stop until cascade completes.
    // The cascade's completion callback checks manualStopPendingRef and handles it.
    if (!windUpsCompleteRef.current) {
      // Dispatch manual stop action so UI reflects the press
      dispatch(featureSpinManualStop());
      return;
    }

    // Clear the auto-stop timeout — we're stopping now
    if (autoStopTimeoutRef.current) {
      clearTimeout(autoStopTimeoutRef.current);
      autoStopTimeoutRef.current = null;
    }

    // Dispatch manual stop action
    dispatch(featureSpinManualStop());

    // Check if API data already arrived
    const state = store.getState();
    if (state.spin.featureSpinStatus === "succeeded") {
      // Data is here — stop with manual (tight) cascade
      triggerCascadingStops(true);
      return;
    }

    // Data not yet arrived — subscribe and stop when it does
    const unsubscribe = store.subscribe(() => {
      const currentState = store.getState();
      if (currentState.spin.featureSpinStatus === "succeeded") {
        // API data arrived — stop with manual (tight) cascade
        triggerCascadingStops(true);
        if (manualStopUnsubRef.current) {
          manualStopUnsubRef.current();
          manualStopUnsubRef.current = null;
        }
      } else if (currentState.spin.featureSpinStatus === "failed") {
        // API error — just clean up
        if (manualStopUnsubRef.current) {
          manualStopUnsubRef.current();
          manualStopUnsubRef.current = null;
        }
      }
    });
    manualStopUnsubRef.current = unsubscribe;
  }, [dispatch, triggerCascadingStops]);

  // ── Readiness — set featureSpinComplete=true so button is interactive ──

  useEffect(() => {
    // RC components are always mounted (never unmount/remount).
    // On first entry, we wait for Redux readiness flags.
    // On re-entry, readiness flags are reset by resetRoyalCollectState()
    // but components don't re-report, so we use componentsEverReadyRef
    // to remember that they were ready at least once.
    const componentsReady = componentsEverReadyRef.current || (
      royalCollectSymbolsReady &&
      royalCollectReelsReady &&
      royalCollectOverlayReelsReady
    );

    if (
      royalCollectActive &&
      !sceneTransitionActive &&
      componentsReady &&
      !readyInitializedRef.current
    ) {
      readyInitializedRef.current = true;
      componentsEverReadyRef.current = true; // Remember for future sessions

       // Trigger initial collection of coins from the trigger round
      if (initialReels && initialReels.length > 0 && !initialCollectionDoneRef.current) {
        initialCollectionDoneRef.current = true;
        // Block input immediately — no spins until initial collection finishes
        dispatch(setCriticalAnimationPlaying(true));
        triggerCollection(initialReels, () => {
          dispatch(setFeatureSpinComplete({ complete: true, bet }));
          // Initial collection + counters settled — spin immediately
          if (handleSpinRef.current) {
            handleSpinRef.current();
          }
        });
      } else {
        dispatch(setFeatureSpinComplete({ complete: true, bet }));
        // No initial collection — schedule first spin directly
        scheduleNextSpin(true);
      }
    } else if (!royalCollectActive && readyInitializedRef.current) {
      readyInitializedRef.current = false;
      initialCollectionDoneRef.current = false;
    }
  }, [
    royalCollectActive,
    sceneTransitionActive,
    royalCollectSymbolsReady,
    royalCollectReelsReady,
    royalCollectOverlayReelsReady,
    dispatch,
    bet,
    initialReels,
    triggerCollection,
  ]);

  // ── Autoplay: keep ref to latest handleSpin ──

  useEffect(() => {
    handleSpinRef.current = handleSpin;
  }, [handleSpin]);

  // ── Spin Complete Cleanup ─────────────────────────────────────────────

  useEffect(() => {
    const isTransitionToComplete =
      featureSpinComplete && !prevSpinCompleteRef.current;

    if (isTransitionToComplete) {
      if (autoStopTimeoutRef.current) {
        clearTimeout(autoStopTimeoutRef.current);
        autoStopTimeoutRef.current = null;
      }
    }

    prevSpinCompleteRef.current = featureSpinComplete;
  }, [featureSpinComplete]);

  // ── Feature Start/End Cleanup ─────────────────────────────────────────

  useEffect(() => {
    if (royalCollectActive) {
      // Reset all state for new session
      isSpinningRef.current = false;
      spinningCountRef.current = 0;
      completedCountRef.current = 0;
      coinLandedThisSpinRef.current = false;

      // Clear any stale autoplay timeout from previous session
      if (autoplayTimeoutRef.current) {
        clearTimeout(autoplayTimeoutRef.current);
        autoplayTimeoutRef.current = null;
      }
    } else {
      // Cleanup on feature end
      isSpinningRef.current = false;
      spinningCountRef.current = 0;
      completedCountRef.current = 0;

      if (autoStopTimeoutRef.current) {
        clearTimeout(autoStopTimeoutRef.current);
        autoStopTimeoutRef.current = null;
      }

      stopTimeoutsRef.current.forEach((id) => clearTimeout(id));
      stopTimeoutsRef.current.clear();

      // Clean up manual stop subscription
      if (manualStopUnsubRef.current) {
        manualStopUnsubRef.current();
        manualStopUnsubRef.current = null;
      }
      manualStopPendingRef.current = false;

      // Clear autoplay timeout
      if (autoplayTimeoutRef.current) {
        clearTimeout(autoplayTimeoutRef.current);
        autoplayTimeoutRef.current = null;
      }

      // Clean up per-royal counter tickers (prevents stale ticker callbacks)
      counterTickersRef.current.forEach((tickerFn) => {
        PIXI.Ticker.shared.remove(tickerFn);
      });
      counterTickersRef.current.clear();

      // Reset per-royal accumulated values and cached position
      royalValuesRef.current.clear();
      knownRoyalsRef.current.clear();
      royalSymbolIndexRef.current = null;
      royalSymbolIdRef.current = null;
      isCollectingRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [royalCollectActive, dispatch, bet]);

  // ── Unmount Cleanup ───────────────────────────────────────────────────

  useEffect(() => {
    const stopTimeoutsMap = stopTimeoutsRef.current;
    const counterTickersMap = counterTickersRef.current;

    return () => {
      if (autoStopTimeoutRef.current) {
        clearTimeout(autoStopTimeoutRef.current);
        autoStopTimeoutRef.current = null;
      }
      stopTimeoutsMap.forEach((id) => clearTimeout(id));
      stopTimeoutsMap.clear();

      // Clean up manual stop subscription
      if (manualStopUnsubRef.current) {
        manualStopUnsubRef.current();
        manualStopUnsubRef.current = null;
      }

      // Clean up per-royal counter tickers
      counterTickersMap.forEach((tickerFn) => {
        PIXI.Ticker.shared.remove(tickerFn);
      });
      counterTickersMap.clear();
    };
  }, []);

  // ── Context Value ─────────────────────────────────────────────────────

  const contextValue = useMemo(() => {
    if (!FEATURES_CONFIG.ROYAL_COLLECT.ENABLED) {
      return {
        handleSpin: () => {},
        handleManualStop: () => {},
        handleSymbolStopComplete: () => {},
      };
    }

    return {
      handleSpin,
      handleManualStop,
      handleSymbolStopComplete,
    };
  }, [handleSpin, handleManualStop, handleSymbolStopComplete]);

  return (
    <RoyalCollectControlContext.Provider value={contextValue}>
      {children}
    </RoyalCollectControlContext.Provider>
  );
};

export const useRoyalCollectControl = () => {
  const context = useContext(RoyalCollectControlContext);
  if (!context) {
    throw new Error(
      "useRoyalCollectControl must be used within a RoyalCollectControlContextProvider",
    );
  }
  return context;
};
